import firebase from "firebase/compat/app";
import { db, functions } from "../../firebase";

// Sección Pedidos (/pedidos): locales de comida con su menú, carrito y
// seguimiento del pedido. El pedido lo crea la Cloud Function placeFoodOrder
// (functions/foodOrders.js), que recalcula los precios desde el menú.

export const FOOD_CATEGORIES = [
  { id: "pizza", label: "Pizzas", emoji: "🍕" },
  { id: "hamburguesas", label: "Hamburguesas", emoji: "🍔" },
  { id: "empanadas", label: "Empanadas", emoji: "🥟" },
  { id: "parrilla", label: "Parrilla", emoji: "🥩" },
  { id: "sushi", label: "Sushi", emoji: "🍣" },
  { id: "pastas", label: "Pastas", emoji: "🍝" },
  { id: "milanesas", label: "Milanesas", emoji: "🍽️" },
  { id: "saludable", label: "Saludable", emoji: "🥗" },
  { id: "cafe", label: "Café y panadería", emoji: "☕" },
  { id: "helados", label: "Helados y postres", emoji: "🍦" },
  { id: "bebidas", label: "Bebidas", emoji: "🥤" },
  { id: "otros", label: "Otros", emoji: "🍴" },
];

// Súper y almacenes: mismo esquema que los locales de comida (kind: "market"),
// el menú son los productos y sus secciones, las góndolas.
export const MARKET_CATEGORIES = [
  { id: "supermercado", label: "Supermercados", emoji: "🛒" },
  { id: "almacen", label: "Almacenes", emoji: "🏪" },
  { id: "verduleria", label: "Verdulerías", emoji: "🥬" },
  { id: "carniceria", label: "Carnicerías", emoji: "🥩" },
  { id: "panaderia", label: "Panaderías", emoji: "🥖" },
  { id: "kiosco", label: "Kioscos y bebidas", emoji: "🧃" },
  { id: "dietetica", label: "Dietéticas", emoji: "🌾" },
  { id: "mascotas", label: "Mascotas", emoji: "🐾" },
  { id: "farmacia", label: "Farmacia y perfumería", emoji: "💊" },
];

export const STORE_KINDS = [
  { id: "food", label: "Comida", emoji: "🍔" },
  { id: "market", label: "Súper", emoji: "🛒" },
];

export const storeKind = (restaurant) => (restaurant?.kind === "market" ? "market" : "food");
export const categoriesFor = (kind) => (kind === "market" ? MARKET_CATEGORIES : FOOD_CATEGORIES);

export const MEAL_TYPES = [
  { id: "desayuno", label: "Desayuno", from: 6 * 60, to: 11 * 60 },
  { id: "almuerzo", label: "Almuerzo", from: 11 * 60, to: 16 * 60 },
  { id: "merienda", label: "Merienda", from: 16 * 60, to: 19 * 60 + 30 },
  { id: "cena", label: "Cena", from: 19 * 60 + 30, to: 30 * 60 },
];

export const categoryOf = (id) =>
  FOOD_CATEGORIES.find((c) => c.id === id) || MARKET_CATEGORIES.find((c) => c.id === id) || FOOD_CATEGORIES[FOOD_CATEGORIES.length - 1];

const minutesNow = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};

// Comida que toca según la hora (de madrugada cuenta como cena).
export const currentMealType = () => {
  const now = minutesNow();
  const adjusted = now < 6 * 60 ? now + 24 * 60 : now;
  return (MEAL_TYPES.find((m) => adjusted >= m.from && adjusted < m.to) || MEAL_TYPES[3]).id;
};

// Minutos desde la medianoche en la zona horaria del local (la guarda el
// panel al crearlo); sin zona, la del dispositivo.
const minutesIn = (timeZone) => {
  if (!timeZone) return minutesNow();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    return Number(parts.find((p) => p.type === "hour").value) * 60 + Number(parts.find((p) => p.type === "minute").value);
  } catch {
    return minutesNow();
  }
};

const toMinutes = (value) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || ""));
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

// Mismo criterio que isOpenNow en functions/foodOrders.js.
export const isRestaurantOpen = (restaurant) => {
  if (!restaurant || restaurant.acceptingOrders === false || restaurant.ownerPlanActive === false) return false;
  const open = toMinutes(restaurant.openTime);
  const close = toMinutes(restaurant.closeTime);
  if (open == null || close == null || open === close) return true;
  const now = minutesIn(restaurant.timeZone);
  return open < close ? now >= open && now < close : now >= open || now < close;
};

// La app se usa en muchos países: cada local elige su moneda (código ISO).
export const CURRENCIES = [
  "ARS", "BOB", "BRL", "CLP", "COP", "CRC", "DOP", "GTQ", "HNL", "MXN", "NIO", "PAB", "PEN", "PYG", "UYU", "VES",
  "USD", "CAD", "EUR", "GBP", "CHF", "AUD", "NZD", "JPY", "CNY", "INR", "KRW", "ZAR", "TRY", "ILS", "AED",
];

const REGION_CURRENCY = {
  AR: "ARS", BO: "BOB", BR: "BRL", CL: "CLP", CO: "COP", CR: "CRC", DO: "DOP", GT: "GTQ", HN: "HNL", MX: "MXN",
  NI: "NIO", PA: "PAB", PE: "PEN", PY: "PYG", UY: "UYU", VE: "VES", EC: "USD", SV: "USD", PR: "USD", US: "USD",
  CA: "CAD", GB: "GBP", CH: "CHF", AU: "AUD", NZ: "NZD", JP: "JPY", CN: "CNY", IN: "INR", KR: "KRW", ZA: "ZAR",
  TR: "TRY", IL: "ILS", AE: "AED", ES: "EUR", FR: "EUR", DE: "EUR", IT: "EUR", PT: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", IE: "EUR", FI: "EUR", GR: "EUR",
};

// Moneda sugerida según el país del idioma del navegador (es-MX → MXN).
export const guessCurrency = () => {
  const languages = typeof navigator !== "undefined" ? navigator.languages || [navigator.language] : [];
  for (const tag of languages) {
    const region = String(tag || "").split("-")[1]?.toUpperCase();
    if (REGION_CURRENCY[region]) return REGION_CURRENCY[region];
  }
  return "USD";
};

export const deviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

// Los locales viejos (sin moneda) son de Argentina, donde arrancó la sección.
export const formatMoney = (value, currency = "ARS") => {
  const amount = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "ARS",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
};

// Precio escrito a mano con el formato de cualquier país: "1.500", "1,500",
// "1.500,50" o "1,500.50". Si hay punto y coma, el último es el decimal; si
// hay uno solo seguido de exactamente 3 dígitos, es de miles.
export const parsePrice = (value) => {
  let text = String(value ?? "").replace(/[^\d.,]/g, "");
  const lastDot = text.lastIndexOf(".");
  const lastComma = text.lastIndexOf(",");
  if (lastDot >= 0 && lastComma >= 0) {
    const decimal = lastDot > lastComma ? "." : ",";
    const thousands = decimal === "." ? "," : ".";
    text = text.split(thousands).join("").replace(decimal, ".");
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? "." : ",";
    const parts = text.split(sep);
    text = parts.length > 2 || parts[parts.length - 1].length === 3 ? parts.join("") : parts.join(".");
  }
  const n = Number(text);
  return Number.isFinite(n) ? n : 0;
};

export const ORDER_STATUS = {
  pending: { label: "Esperando al local", color: "warning" },
  accepted: { label: "En preparación", color: "info" },
  on_the_way: { label: "En camino", color: "info" },
  ready: { label: "Listo para retirar", color: "info" },
  delivered: { label: "Entregado", color: "success" },
  rejected: { label: "Rechazado", color: "error" },
  cancelled: { label: "Cancelado", color: "default" },
};

export const orderSteps = (mode) =>
  mode === "takeaway"
    ? ["pending", "accepted", "ready", "delivered"]
    : ["pending", "accepted", "on_the_way", "delivered"];

// Próximo estado que el local puede marcar (botón principal del panel).
export const nextOrderStatus = (order) => {
  const steps = orderSteps(order.mode);
  const index = steps.indexOf(order.status);
  return index >= 0 && index < steps.length - 1 ? steps[index + 1] : null;
};

export const NEXT_STATUS_ACTION = {
  accepted: "Aceptar pedido",
  on_the_way: "Salió el envío",
  ready: "Listo para retirar",
  delivered: "Confirmar entrega con código",
};

export const timestampMs = (value) => (value?.toMillis ? value.toMillis() : value?.seconds ? value.seconds * 1000 : 0);

const mapDoc = (doc) => ({ id: doc.id, ...doc.data() });

export const subscribeRestaurants = (onChange, onError) =>
  db.collection("restaurants").where("active", "==", true).onSnapshot(
    // Los locales sin plan activo no se listan (ver syncRestaurantPlan en
    // functions/foodOrders.js); el filtro va acá porque los viejos no tienen el campo.
    (snap) => onChange(snap.docs.map(mapDoc).filter((r) => r.ownerPlanActive !== false)),
    onError
  );

export const subscribeRestaurant =(id, onChange, onError) =>
  db.collection("restaurants").doc(id).onSnapshot(
    (snap) => onChange(snap.exists ? mapDoc(snap) : null),
    onError
  );

export const subscribeMenu = (restaurantId, onChange, onError) =>
  db.collection("restaurants").doc(restaurantId).collection("menu").onSnapshot(
    (snap) => onChange(snap.docs.map(mapDoc).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
    onError
  );

export const fetchOwnRestaurant = async (uid) => {
  const snap = await db.collection("restaurants").where("uid", "==", uid).limit(1).get();
  return snap.empty ? null : mapDoc(snap.docs[0]);
};

export const saveRestaurant = async (id, data) => {
  const now = firebase.firestore.FieldValue.serverTimestamp();
  if (id) {
    await db.collection("restaurants").doc(id).update({ ...data, updatedAt: now });
    return id;
  }
  const ref = await db.collection("restaurants").add({ ...data, createdAt: now, updatedAt: now });
  return ref.id;
};

export const saveMenuItem = async (restaurantId, itemId, data) => {
  const menu = db.collection("restaurants").doc(restaurantId).collection("menu");
  if (itemId) return menu.doc(itemId).update(data);
  return menu.add({ ...data, order: Date.now() });
};

export const deleteMenuItem = (restaurantId, itemId) =>
  db.collection("restaurants").doc(restaurantId).collection("menu").doc(itemId).delete();

const sortNewest = (list) => list.sort((a, b) => timestampMs(b.createdAt) - timestampMs(a.createdAt));

export const subscribeOrdersAsBuyer = (uid, onChange, onError) =>
  db.collection("foodOrders").where("buyerId", "==", uid).onSnapshot(
    (snap) => onChange(sortNewest(snap.docs.map(mapDoc))),
    onError
  );

export const subscribeOrdersAsRestaurant = (uid, onChange, onError) =>
  db.collection("foodOrders").where("restaurantOwnerId", "==", uid).onSnapshot(
    (snap) => onChange(sortNewest(snap.docs.map(mapDoc))),
    onError
  );

export const subscribeOrder = (orderId, onChange, onError) =>
  db.collection("foodOrders").doc(orderId).onSnapshot(
    (snap) => onChange(snap.exists ? mapDoc(snap) : null),
    onError
  );

export const setOrderStatus = (orderId, status) =>
  db.collection("foodOrders").doc(orderId).update({
    status,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

export const placeFoodOrder = async (payload) => {
  const result = await functions.httpsCallable("placeFoodOrder")(payload);
  return result.data.orderId;
};

// Carrito por local, guardado en el navegador (si no se puede, queda en memoria).
const cartKey = (restaurantId) => `notiline_cart_${restaurantId}`;

export const loadCart = (restaurantId) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(cartKey(restaurantId)) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const storeCart = (restaurantId, cart) => {
  try {
    if (cart.length) localStorage.setItem(cartKey(restaurantId), JSON.stringify(cart));
    else localStorage.removeItem(cartKey(restaurantId));
  } catch {
    // Sin almacenamiento (modo privado): el carrito vive mientras la página esté abierta.
  }
};

// Código de entrega (solo lo puede leer el comprador, ver firestore.rules).
export const subscribeDeliveryCode = (orderId, onChange) =>
  db.collection("foodOrders").doc(orderId).collection("private").doc("delivery").onSnapshot(
    (snap) => onChange(snap.data()?.code || ""),
    () => onChange("")
  );

// El local carga el código que le dice el cliente; devuelve { ok, attemptsLeft }.
export const confirmFoodDelivery = async (orderId, code) => {
  const result = await functions.httpsCallable("confirmFoodDelivery")({ orderId, code });
  return result.data;
};

export const updateRiderLocation = (orderId, lat, lng) =>
  db.collection("foodOrders").doc(orderId).update({
    riderLocation: { lat, lng, at: Date.now() },
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

// Recorrido por calles entre dos puntos con el servidor público de OSRM.
// Devuelve [[lat, lng], ...] y la duración en minutos, o null si falla
// (el mapa dibuja entonces una línea recta).
export const fetchRoute = async (from, to) => {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
    const response = await fetch(url);
    const data = await response.json();
    const route = data?.routes?.[0];
    if (!route) return null;
    return {
      points: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
      minutes: Math.max(1, Math.round(route.duration / 60)),
    };
  } catch {
    return null;
  }
};

export const directionsUrl = (location) =>
  `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}&travelmode=driving`;
